//core
import {createSlice, createAsyncThunk} from "@reduxjs/toolkit";
import {ActionReducerMapBuilder} from "@reduxjs/toolkit/src/mapBuilders";
import {NoInfer} from "@reduxjs/toolkit/src/tsHelpers";

//service
import { reattributedPlayersService } from "../../../services/admin/reattributedPlayers.service";
import {setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState} from "../../common/ui";
import {setUser} from "../../common/user";

export type reattributedPlayersSliceState = {
  list: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  create: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
}

const initialState: reattributedPlayersSliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  create: {
    data: null,
    error: null,
    isFetching: false,
  },
}

export const getList: any = createAsyncThunk(
  'reattributedPlayers/getList',
  async (payload, { rejectWithValue, dispatch}) => {
    try {
      const response: any = await reattributedPlayersService.getList(payload);
      const data: any = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);


export const createData: any = createAsyncThunk(
  'reattributedPlayers/createData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await reattributedPlayersService.create(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      );
      return rejectWithValue(error)
    }
  }
);

//slice
const reattributedPlayersSlice = createSlice({
  name: 'reattributedPlayers',
  initialState: initialState,
  reducers: {
    reset: () => initialState,
    clearListState(state) {
      state.list.data = null;
      state.list.isFetching = false;
      state.list.error = null;
    },
    clearCreateState(state) {
      state.create.data = null;
      state.create.error = null;
      state.create.isFetching = false;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<reattributedPlayersSliceState>>) => {
    builder.addCase(getList.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getList.fulfilled, (state, action ) => {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = action.payload;
    });
    builder.addCase(getList.rejected, (state, action ) => {
      state.list.error = action.payload;
    });

    builder.addCase(createData.pending, (state ) => {
      state.create.isFetching = true;
      state.create.error = null;
    });
    builder.addCase(createData.fulfilled, (state , action) => {
      state.create.error = null;
      state.create.isFetching = false;
      state.create.data = action.payload;
    });
    builder.addCase(createData.rejected, (state , action) => {
      state.create.isFetching = false;
      state.create.error = action.payload;
    });
  },
});

export default reattributedPlayersSlice.reducer;

export const {
  reset,
  clearListState,
  clearCreateState,
} = reattributedPlayersSlice.actions;
